import { useEffect, useState, useCallback } from "react";
import { MessageSquare, Plus, Search, FileText, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Loader } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import Modal, { TextField, TextArea } from "@/components/Modal";
import { ConversationThread } from "@/pages/MessagingPage";
import { toast } from "sonner";

const ROLE_LABEL = { client: "Client", professional: "Professionnel", livreur: "Livreur", influencer: "Influenceur" };

export default function AdminMessaging() {
  const [convs, setConvs] = useState(null);
  const [tab, setTab] = useState("all");
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const load = useCallback(() => api.get("/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([])), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const list = tab === "unread" ? (convs || []).filter((c) => c.unread_staff) : (convs || []);
  const unreadCount = (convs || []).filter((c) => c.unread_staff).length;

  return (
    <div>
      <div className="flex items-center justify-between">
        <PageTitle>Messagerie</PageTitle>
        <div className="flex gap-2">
          <button onClick={() => setShowTemplates(true)} className="bg-white border border-slate-200 text-slate-700 font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="templates-btn"><FileText className="w-4 h-4" /> Modèles</button>
          <button onClick={() => setShowNew(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="admin-new-conversation-btn"><Plus className="w-4 h-4" /> Nouveau message</button>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("all")} data-testid="msg-tab-all" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "all" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Toutes ({(convs || []).length})</button>
        <button onClick={() => setTab("unread")} data-testid="msg-tab-unread" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "unread" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Non lues ({unreadCount})</button>
      </div>
      {!convs ? <Loader /> : (
        <Panel className="divide-y divide-slate-100">
          {list.length === 0 && <p className="p-4 text-sm text-slate-400">Aucune conversation.</p>}
          {list.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} className="w-full text-left px-4 py-3 hover:bg-slate-50 transition relative" data-testid={`admin-conversation-${c.id}`}>
              {c.unread_staff && <span className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-[#C85A32]" />}
              <div className="flex items-center gap-2">
                <span className="font-num text-xs text-slate-400">{c.conversation_number}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ROLE_LABEL[c.user_role] || c.user_role}</span>
                <span className="ml-auto text-xs text-slate-400 mr-4">{formatDate(c.last_at)}</span>
              </div>
              <p className="font-semibold text-slate-800 mt-1 pr-6">{c.subject}</p>
              <p className="text-xs text-slate-500">{c.user_name} — <span className="text-slate-400 line-clamp-1 inline">{c.last_message}</span></p>
            </button>
          ))}
        </Panel>
      )}
      {active && <ConversationThread id={active} staff onClose={() => setActive(null)} onChanged={load} />}
      {showNew && <NewAdminConversation onClose={() => setShowNew(false)} onDone={load} />}
      {showTemplates && <TemplatesModal onClose={() => setShowTemplates(false)} />}
    </div>
  );
}

function NewAdminConversation({ onClose, onDone }) {
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState([]);
  const [target, setTarget] = useState(null);
  const [f, setF] = useState({ subject: "", message: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    const t = setTimeout(() => { api.get("/messaging/contacts", { params: q ? { q } : {} }).then((r) => setContacts(r.data)).catch(() => setContacts([])); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return toast.error("Choisissez un destinataire");
    try { await api.post("/conversations", { ...f, target_user_id: target.user_id }); toast.success("Message envoyé"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title="Nouveau message" onClose={onClose} testid="admin-conversation-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        {!target ? (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Destinataire</label>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un utilisateur…" className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm" data-testid="contact-search" />
            </div>
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {contacts.length === 0 && <p className="p-3 text-xs text-slate-400">Aucun utilisateur.</p>}
              {contacts.map((u) => (
                <button key={u.user_id} type="button" onClick={() => setTarget(u)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" data-testid={`contact-${u.user_id}`}>
                  <span className="font-semibold">{u.name}</span> <span className="text-xs text-slate-400">{ROLE_LABEL[u.role] || u.role} · {u.email}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-[#FDF3EE] rounded-lg px-3 py-2 text-sm" data-testid="selected-contact">
            À : <strong>{target.name}</strong> <span className="text-xs text-slate-400">({ROLE_LABEL[target.role] || target.role})</span>
            <button type="button" onClick={() => setTarget(null)} className="ml-auto text-xs text-[#C85A32] font-semibold">Changer</button>
          </div>
        )}
        <TextField label="Sujet" value={f.subject} onChange={set("subject")} required testid="admin-conversation-subject" />
        <TextArea label="Message" value={f.message} onChange={set("message")} required testid="admin-conversation-message" rows={4} />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="admin-conversation-submit">Envoyer</button>
      </form>
    </Modal>
  );
}


function TemplatesModal({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState({ title: "", body: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const load = useCallback(() => api.get("/message-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([])), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/message-templates", f); toast.success("Modèle ajouté"); setF({ title: "", body: "" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (id) => { try { await api.delete(`/message-templates/${id}`); load(); } catch (err) { toast.error(apiError(err)); } };

  return (
    <Modal title="Modèles de réponses" onClose={onClose} testid="templates-modal" size="lg">
      <div className="space-y-3">
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {templates.length === 0 && <p className="text-sm text-slate-400">Aucun modèle.</p>}
          {templates.map((t) => (
            <div key={t.id} className="flex items-start gap-2 bg-slate-50 rounded-lg p-3" data-testid={`template-row-${t.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-slate-800">{t.title}</p>
                <p className="text-xs text-slate-500 line-clamp-2">{t.body}</p>
              </div>
              <button onClick={() => del(t.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg shrink-0" data-testid={`template-delete-${t.id}`} title="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <form onSubmit={add} className="space-y-2 border-t border-slate-100 pt-3">
          <TextField label="Titre du modèle" value={f.title} onChange={set("title")} required testid="template-title" />
          <TextArea label="Contenu" value={f.body} onChange={set("body")} required testid="template-body" rows={3} />
          <button className="w-full bg-[#0F172A] text-white font-bold py-2.5 rounded-lg" data-testid="template-add">Ajouter le modèle</button>
        </form>
      </div>
    </Modal>
  );
}
